import useAgentStore from "@/Modules/Agent/Store";

import { config } from "@/Common/envConfig";
import { hasValue } from "@/Utils/Objects";
import { log, danger } from "@/Utils/Debug";
import { waitFor } from "@/Utils/Promises";

interface HubSpotWidgetStatus {
  loaded: boolean;
  pending: boolean;
}

interface HubSpotWidget {
  open: () => void;
  close: () => void;
  load: () => void;
  refresh: () => void;
  remove: () => void;
  status: () => HubSpotWidgetStatus;
}
export interface HubSpot {
  widget: HubSpotWidget;
}

export const addHubSpotScript = (): void => {
  if (isHubSpotInjected()) {
    log("HubSpot injected?");
    return;
  }

  const script = document.createElement("script");
  script.src = `https://js-eu1.hs-scripts.com/${config.hubspotCode}.js`;
  script.async = true;
  script.defer = true;
  script.type = "text/javascript";
  script.id = "hs-script-loader";

  script.onerror = (e) => {
    log("Error loading HubSpot script", e);
  };

  document.body.appendChild(script);
};

export const waitForHubSpotLoaded = async (timeout = 10000): Promise<void> => {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    const widget = getWidget();

    if (widget?.status()?.loaded) {
      log("HubSpot loaded");
      ubicateHubSpot();

      return;
    }

    await waitFor(0.2);
  }

  throw new Error("HubSpot didn't load in time");
};

export const isHubSpotInjected = (): boolean => {
  return hasValue(window) && hasValue(window.HubSpotConversations) && hasValue(getWidget());
};

export const isHubSpotLoaded = (): boolean => {
  return !!getWidget()?.status()?.loaded;
};

const getWidget = (): HubSpotWidget | undefined => {
  const conversations = window.HubSpotConversations;

  if (!hasValue(conversations)) return undefined;

  const widget = conversations.widget;

  return widget && typeof widget.status === "function" ? widget : undefined;
};

export const removeHubSpot = (removeScript = true): void => {
  try {
    const widget = getWidget();

    if (widget?.status()?.loaded) {
      widget.close();
      widget.remove();
      log("HubSpot deleted");
    }

    if (removeScript) {
      const script = document.getElementById("hs-script-loader");
      if (script?.parentNode) {
        script.parentNode.removeChild(script);
        log("HubSpot script removed from DOM.");
      }
    }
  } catch (e) {
    danger("Error deleting HubSpot:", e);
  }
};

export const refreshHubSpot = (): void => {
  try {
    const widget = getWidget();

    widget?.refresh();

    log("HubSpot refreshed");
  } catch (e) {
    danger("Error refresh HubSpot:", e);
  }
};

export const ubicateHubSpot = (): void => {
  const agentStore = useAgentStore();
  const hubspotIframe = document.getElementById("hubspot-messages-iframe-container");
  const position = agentStore.isBotChatOpen ? "360px" : "0px";

  if (hasValue(hubspotIframe)) {
    hubspotIframe.style.setProperty("right", position, "important");
    hubspotIframe.style.setProperty("transform", "scale(0.85)", "important");
    hubspotIframe.style.setProperty("bottom", "6vh", "important");
  }
};

export const closeHubSpot = (): void => {
  try {
    const widget = getWidget();

    if (widget?.status()?.loaded) {
      widget.close();
      log("HubSpot closed");
    }
  } catch (e) {
    danger("Error closing HubSpot:", e);
  }
};
